/**
 * LockedIdentityCard — the locked "Patient Data [INSi]" card.
 *
 * Read-only identity (first/middle/last name, suffix, DOB, birth sex) for a
 * patient whose identity comes from INSi. Locked: the only way to change it is
 * "Update INSi Data" (re-search). Shown on create (T08), edit re-search (T09),
 * and edit-on-open for a patient that already has an INS.
 *
 * Renders through the shared `PatientDataCard` shell (locked/blue variant) so it
 * stays visually aligned with the editable identity card.
 *
 * Props (the name MODEL, so multiple first names display in full):
 *   - name:          { firstName: string[]|string, middleName, lastName, suffix }
 *   - dateNaissance: 'YYYY-MM-DD'
 *   - sexe:          'M' | 'F'
 *   - insIdentifier: { value, type }
 *   - onUpdate:      re-opens the InsiSearchModal to replace the selection.
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';

import { Box as MuiBox } from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
  EditOutlined as EditIcon,
  CalendarTodayOutlined as CalendarIcon,
  PlaceOutlined as PlaceIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from '@mui/icons-material';

import colors from 'lib/colors';
import PatientDataCard from 'components/insi/PatientDataCard';
import { INSI_STATUS_COLORS } from 'components/insi/insiStatusColors';

/** Format a 15-digit INS matricule readably: "X XX XX XX XXX XXX XX". */
const formatMatricule = (value) => {
  if (!value) return '';
  const digits = value.replace(/\s+/g, '');
  if (digits.length !== 15) return value;
  return [
    digits.slice(0, 1),
    digits.slice(1, 3),
    digits.slice(3, 5),
    digits.slice(5, 7),
    digits.slice(7, 10),
    digits.slice(10, 13),
    digits.slice(13, 15),
  ].join(' ');
};

/** Format an INSi date (YYYY-MM-DD) as dd/MM/yyyy for display. */
const formatDob = (value) => {
  if (!value) return '-';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : value;
};

/** Join the first-name array (can hold multiple prénoms) into one string. */
const joinFirstNames = (firstName) => {
  if (Array.isArray(firstName)) return firstName.filter(Boolean).join(' ');
  return firstName || '';
};

/** A single locked field: label + grey read-only box (value) + padlock. */
const LockedField = ({ label, value, startIcon }) => (
  <SField>
    <span className="label">{label}</span>
    <div className="box">
      {startIcon}
      <span className="value">{value || '-'}</span>
      <LockIcon className="lock" />
    </div>
  </SField>
);

LockedField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  startIcon: PropTypes.node,
};

export default function LockedIdentityCard({
  name,
  dateNaissance,
  sexe,
  birthPlace,
  birthPlaceInseeCode,
  insIdentifier,
  status,
  onUpdate,
}) {
  const { t } = useTranslation();
  const isMale = sexe === 'M';
  const isFemale = sexe === 'F';
  // Colour the card by status (defaults to Récupérée blue when unknown).
  const { accent, bg: light } =
    INSI_STATUS_COLORS[status] || INSI_STATUS_COLORS.RECUPEREE;

  const banner = (
    <SBanner data-id="insi-locked-banner">
      <InfoIcon className="icon" />
      <span>{t('Identity retrieved via INSi - identity fields locked')}</span>
    </SBanner>
  );

  // "Update INSi Data" triggers a live INSi re-search, so it is only offered
  // when the caller passes `onUpdate` (gated on INSi access). Viewing the locked
  // identity itself needs no PSC session, so the card still renders without it.
  const footer = onUpdate ? (
    <SUpdateButton type="button" data-id="insi-update-data" onClick={onUpdate}>
      <EditIcon />
      {t('Update INSi Data')}
    </SUpdateButton>
  ) : null;

  return (
    <PatientDataCard
      variant="locked"
      badge
      banner={banner}
      footer={footer}
      dataId="insi-locked-identity"
      accent={accent}
      accentLight={light}
    >
      {/* INSi identity = birth surname + a LIST of prénoms (no "middle name").
          Give the prénoms their own full-width row, labelled multi-value. */}
      <SRow>
        <LockedField
          label={t('First name(s)')}
          value={joinFirstNames(name?.firstName)}
        />
      </SRow>
      <SRow>
        <LockedField label={t('Last name')} value={name?.lastName} />
        <LockedField label={t('Suffix')} value={name?.suffix} />
      </SRow>
      <SRow>
        <LockedField
          label={t('Date of birth')}
          value={formatDob(dateNaissance)}
          startIcon={<CalendarIcon className="prefix" />}
        />
        <SField>
          <span className="label">{t('Birth sex')}</span>
          <div className="sex">
            <button
              type="button"
              className={isMale ? 'selected' : ''}
              disabled
              aria-label={t('Male')}
            >
              <MaleIcon />
            </button>
            <button
              type="button"
              className={isFemale ? 'selected' : ''}
              disabled
              aria-label={t('Female')}
            >
              <FemaleIcon />
            </button>
          </div>
        </SField>
      </SRow>
      <SRow>
        <LockedField
          label={t('Place of birth')}
          value={birthPlace?.city || birthPlace?.country || '-'}
          startIcon={<PlaceIcon className="prefix" />}
        />
        <LockedField
          label={t('COG Code')}
          value={birthPlaceInseeCode || '-'}
        />
      </SRow>

      <SInsNumber data-id="insi-locked-ins-number">
        <span className="label">{t('INS Number')}</span>
        <span className="value">{formatMatricule(insIdentifier?.value)}</span>
      </SInsNumber>
    </PatientDataCard>
  );
}

LockedIdentityCard.propTypes = {
  name: PropTypes.shape({
    firstName: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    suffix: PropTypes.string,
  }).isRequired,
  dateNaissance: PropTypes.string,
  sexe: PropTypes.oneOf(['M', 'F']),
  birthPlace: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  birthPlaceInseeCode: PropTypes.string,
  insIdentifier: PropTypes.shape({
    value: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  status: PropTypes.oneOf(['PROVISOIRE', 'RECUPEREE', 'QUALIFIEE']),
  /** Re-search handler; omit to hide "Update INSi Data" (no INSi access). */
  onUpdate: PropTypes.func,
};

LockedIdentityCard.defaultProps = {
  dateNaissance: undefined,
  sexe: undefined,
  birthPlace: undefined,
  birthPlaceInseeCode: undefined,
  status: undefined,
  onUpdate: undefined,
};

// ---------------------------------------------------------------------------
// Styled — locked fields. Colours come from the --insi-accent / --insi-light
// CSS variables set by PatientDataCard (status-themed); hex fallbacks = blue.
// ---------------------------------------------------------------------------

const SRow = styled(MuiBox)`
  display: flex;
  gap: 30px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const SField = styled(MuiBox)`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #000000;
  }

  .box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 8px;
    border-radius: 6px;
    background: #f7f7f7;
    border: 1px solid #ececec;
    box-sizing: border-box;
  }
  .box .prefix {
    font-size: 20px;
    color: #222222;
  }
  .box .value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #222222;
  }
  .box .lock {
    font-size: 20px;
    color: #c8c8c8;
  }

  .sex {
    display: flex;
    gap: 10px;
  }
  .sex button {
    flex: 1 1 0;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: #ffffff;
    border: 1px solid #c8c8c8;
    color: #222222;
    cursor: not-allowed;
  }
  .sex button.selected {
    background: var(--insi-light, ${colors.blue200});
    border: 1px solid var(--insi-accent, ${colors.blue100});
    color: var(--insi-accent, ${colors.blue100});
  }
`;

const SBanner = styled(MuiBox)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  background: var(--insi-light, ${colors.blue200});
  border: 1px solid var(--insi-accent, ${colors.blue100});
  color: var(--insi-accent, ${colors.blue100});
  font-style: italic;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;

  .icon {
    font-size: 16px;
  }
`;

const SInsNumber = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 24px;
  border-radius: 12px;
  background: var(--insi-light, ${colors.blue200});
  border: 1px solid var(--insi-accent, ${colors.blue100});

  .label {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #222222;
  }
  .value {
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 1px;
    color: var(--insi-accent, ${colors.blue100});
  }
`;

const SUpdateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 40px;
  padding: 10px 16px;
  border-radius: 6px;
  background: #ffffff;
  border: 1.5px solid var(--insi-accent, ${colors.blue100});
  color: var(--insi-accent, ${colors.blue100});
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  svg {
    font-size: 20px;
  }

  :hover {
    background: var(--insi-light, ${colors.blue200});
  }
`;
